import { useGetInstallAttestationCopyQuery } from '@/services/installAttestationCopy';
import { Box, Button, Modal, RadioGroup, Spinner } from '@palmetto/palmetto-components';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { InstallAttestmentType } from 'types';

export interface AttestmentPayloadItem {
  attestmentType: string;
  hasRead: boolean;
  permitSelection?: string;
}

export interface AttestmentPayload {
  items: Record<string, AttestmentPayloadItem>;
  version: string;
}

interface InstallAttestationModalProps {
  isOpen: boolean;
  onDismiss: () => void;
  onAttest: (attestments: AttestmentPayload) => void;
}

const PERMITTING_KEY = InstallAttestmentType.permitting;
const SCROLL_END_THRESHOLD = 10;

export function InstallAttestationModal({ isOpen, onDismiss, onAttest }: InstallAttestationModalProps) {
  const { data, isLoading, isError } = useGetInstallAttestationCopyQuery(
    { program: 'generator' },
    { skip: !isOpen },
  );

  const [hasScrolledToEnd, setHasScrolledToEnd] = useState(false);
  const [permitSelection, setPermitSelection] = useState<string>('');
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen) {
      setHasScrolledToEnd(false);
      setPermitSelection('');
    }
  }, [isOpen]);

  // If the content fits without scrolling, unlock attestation immediately.
  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el || !data) return;
    if (el.scrollHeight <= el.clientHeight + SCROLL_END_THRESHOLD) {
      setHasScrolledToEnd(true);
    }
  }, [data]);

  const itemEntries = useMemo(() => (data ? Object.entries(data.items) : []), [data]);
  const hasPermittingItem = useMemo(() => itemEntries.some(([key]) => key === PERMITTING_KEY), [itemEntries]);

  const permitRadioOptions = useMemo(() => {
    const permitOptions = data?.items[PERMITTING_KEY]?.permitOptions;
    if (!permitOptions) return [];
    return Object.entries(permitOptions).map(([optionKey, option]) => ({
      id: `permit-option-${optionKey}`,
      value: optionKey,
      label: option.title,
    }));
  }, [data]);

  const handleScroll = useCallback((event: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, clientHeight, scrollHeight } = event.currentTarget;
    if (scrollTop + clientHeight >= scrollHeight - SCROLL_END_THRESHOLD) {
      setHasScrolledToEnd(true);
    }
  }, []);

  const handlePermitChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setPermitSelection(event.target.value);
  }, []);

  const canAttest = hasScrolledToEnd && (!hasPermittingItem || permitSelection !== '');

  const handleAttest = useCallback(() => {
    if (!data) return;
    const items = itemEntries.reduce<Record<string, AttestmentPayloadItem>>((acc, [key]) => {
      acc[key] = {
        attestmentType: key,
        hasRead: true,
        ...(key === PERMITTING_KEY && permitSelection ? { permitSelection } : {}),
      };
      return acc;
    }, {});
    onAttest({ items, version: data.version });
    onDismiss();
  }, [data, itemEntries, permitSelection, onAttest, onDismiss]);

  return (
    <Modal isOpen={isOpen} onDismiss={onDismiss} maxWidth="4xl" ariaLabelledBy="installAttestationModalTitle">
      <Modal.Header id="installAttestationModalTitle" title="Install Attestation" onDismiss={onDismiss} />
      <Modal.Body background="secondary">
        {isLoading ? (
          <Box display="flex" justifyContent="center" alignItems="center" padding="2xl">
            <Spinner size="lg" />
          </Box>
        ) : isError || !data ? (
          <Box color="danger" padding="lg">
            There was a problem loading the attestation. Please close this dialog and try again.
          </Box>
        ) : (
          <Box
            ref={scrollContainerRef}
            borderColor="separator"
            borderWidth="sm"
            radius="md"
            padding="lg"
            childGap="lg"
            overflow="auto"
            maxHeight="60vh"
            onScroll={handleScroll}
          >
            {itemEntries.map(([key, entry]) => (
              <Box key={key} childGap="sm">
                <Box>
                  <strong>{entry.title}</strong>
                </Box>
                {entry.body ? <p>{entry.body}</p> : null}
                {key === PERMITTING_KEY && entry.permitOptions ? (
                  <RadioGroup
                    name="permitSelection"
                    value={permitSelection}
                    onChange={handlePermitChange}
                    options={permitRadioOptions}
                  />
                ) : null}
              </Box>
            ))}
          </Box>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" tone="neutral" onClick={onDismiss} id="installAttestationCancelCTA">
          Cancel
        </Button>
        <Button
          variant="primary"
          tone="primary"
          onClick={handleAttest}
          disabled={!canAttest}
          id="installAttestationAttestCTA"
        >
          Attest
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
